import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { ClipboardEvent as ReactClipboardEvent, DragEvent as ReactDragEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BlogRichContent } from "../components/BlogRichContent";
import { BlogRecipeSections } from "../components/BlogRecipeSections";
import { RecipeStructuredEditor } from "../components/RecipeStructuredEditor";
import { Button, Card, ErrorState, PageHeader, SegmentedControl, Skeleton, TextField } from "../components/ui";
import { createBlog, fetchBlogDetail, updateBlog, uploadBlogMedia } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import {
  buildRecipeFallbackBody,
  createEmptyRecipeStructuredContent,
  isImageMedia,
  resolveBlogDisplayStyle,
  resolveMediaAltText,
  sanitizeStructuredContentForSave,
} from "../lib/blogPosts";
import { useShellSession } from "../hooks/useShellSession";
import type { BlogMedia, BlogStructuredContent } from "../types";

type PublishStatus = "DRAFT" | "PUBLISHED";
type PostFormat = "ARTICLE" | "RECIPE";

function tagsFromText(value: string): string[] {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function cleanMarkdownToken(value: string): string {
  return value.replace(/[\[\]\(\)]/g, "").replace(/"/g, "'").trim();
}

function toSafeUrl(source: string): string | null {
  const candidate = source.trim();
  if (!candidate) {
    return null;
  }
  if (candidate.startsWith("/")) {
    return candidate;
  }
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return candidate;
    }
    return null;
  } catch {
    return null;
  }
}

function mediaIdentifier(entry: BlogMedia, index: number): string {
  return `${entry.key || entry.url || "media"}-${index}`;
}

function firstImageMediaUrl(entries: BlogMedia[]): string {
  const match = entries.find((entry) => isImageMedia(entry) && Boolean((entry.url || "").trim()));
  return (match?.url || "").trim();
}

function orderMediaByCover(entries: BlogMedia[], coverUrl: string): BlogMedia[] {
  const normalizedCoverUrl = coverUrl.trim();
  if (!normalizedCoverUrl) {
    return [...entries];
  }
  const prioritized = entries.filter((entry) => (entry.url || "").trim() === normalizedCoverUrl);
  const remainder = entries.filter((entry) => (entry.url || "").trim() !== normalizedCoverUrl);
  return [...prioritized, ...remainder];
}

function mediaCaptionLabel(entry: BlogMedia): string {
  if (entry.caption?.trim()) {
    return entry.caption.trim();
  }
  if (entry.alt_text?.trim()) {
    return entry.alt_text.trim();
  }
  return "No caption";
}

function normalizeInlineWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function normalizeBlockWhitespace(value: string): string {
  return value.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

function joinSerializedSegments(segments: string[]): string {
  return segments.reduce((accumulator, current) => {
    if (!current) {
      return accumulator;
    }
    if (!accumulator) {
      return current;
    }
    const needsSpace = /[A-Za-z0-9)\]]$/.test(accumulator) && /^[A-Za-z0-9(\[]/.test(current);
    return `${accumulator}${needsSpace ? " " : ""}${current}`;
  }, "");
}

function htmlToMarkdown(source: string): string {
  const parser = new DOMParser();
  const documentNode = parser.parseFromString(source, "text/html");

  function childrenAsText(node: HTMLElement): string {
    return joinSerializedSegments(Array.from(node.childNodes).map(serializeNode));
  }

  function serializeNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || "").replace(/\u00a0/g, " ");
    }

    if (!(node instanceof HTMLElement)) {
      return "";
    }

    const tag = node.tagName.toLowerCase();

    if (tag === "script" || tag === "style") {
      return "";
    }

    if (tag === "br") {
      return "\n";
    }

    if (tag === "strong" || tag === "b") {
      const content = normalizeInlineWhitespace(childrenAsText(node));
      return content ? `**${content}**` : "";
    }

    if (tag === "em" || tag === "i") {
      const content = normalizeInlineWhitespace(childrenAsText(node));
      return content ? `*${content}*` : "";
    }

    if (tag === "code" && node.parentElement?.tagName.toLowerCase() !== "pre") {
      const content = normalizeInlineWhitespace(node.textContent || "");
      return content ? `\`${content}\`` : "";
    }

    if (tag === "pre") {
      const content = (node.textContent || "").trim();
      if (!content) {
        return "";
      }
      return `\n\n\`\`\`\n${content}\n\`\`\`\n\n`;
    }

    if (tag === "a") {
      const label = normalizeInlineWhitespace(childrenAsText(node)) || "Link";
      const href = toSafeUrl(node.getAttribute("href") || "");
      return href ? `[${label}](${href})` : label;
    }

    if (tag === "img") {
      const src = toSafeUrl(node.getAttribute("src") || "");
      if (!src) {
        return "";
      }
      const alt = cleanMarkdownToken(node.getAttribute("alt") || "") || "Embedded image";
      const caption = cleanMarkdownToken(node.getAttribute("title") || "");
      const captionToken = caption ? ` "${caption}"` : "";
      return `\n\n![${alt}](${src}${captionToken})\n\n`;
    }

    if (tag === "h1" || tag === "h2" || tag === "h3") {
      const level = tag === "h1" ? "#" : tag === "h2" ? "##" : "###";
      const content = normalizeInlineWhitespace(childrenAsText(node));
      return content ? `\n\n${level} ${content}\n\n` : "";
    }

    if (tag === "blockquote") {
      const content = normalizeBlockWhitespace(childrenAsText(node));
      if (!content) {
        return "";
      }
      const lines = content
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => `> ${line}`)
        .join("\n");
      return lines ? `\n\n${lines}\n\n` : "";
    }

    if (tag === "ul" || tag === "ol") {
      const ordered = tag === "ol";
      const items = Array.from(node.children)
        .filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName.toLowerCase() === "li")
        .map((item, index) => {
          const content = normalizeBlockWhitespace(childrenAsText(item)).replace(/\s*\n\s*/g, " ");
          if (!content) {
            return "";
          }
          return ordered ? `${index + 1}. ${content}` : `- ${content}`;
        })
        .filter(Boolean)
        .join("\n");
      return items ? `\n\n${items}\n\n` : "";
    }

    if (
      tag === "p" ||
      tag === "div" ||
      tag === "section" ||
      tag === "article" ||
      tag === "header" ||
      tag === "footer" ||
      tag === "main" ||
      tag === "aside" ||
      tag === "figure" ||
      tag === "figcaption" ||
      tag === "li"
    ) {
      const content = normalizeBlockWhitespace(childrenAsText(node));
      return content ? `\n\n${content}\n\n` : "";
    }

    return childrenAsText(node);
  }

  const markdown = Array.from(documentNode.body.childNodes)
    .map(serializeNode)
    .join("");

  return normalizeBlockWhitespace(markdown);
}

function isSupportedMediaFile(file: File): boolean {
  const mime = (file.type || "").toLowerCase();
  if (mime.startsWith("image/") || mime.startsWith("video/")) {
    return true;
  }
  const fileName = file.name.toLowerCase();
  return /\.(png|jpe?g|gif|webp|avif|svg|mp4|mov|webm|m4v)$/.test(fileName);
}

function toImageEmbedMarkdown(entry: BlogMedia, fallbackTitle: string): string | null {
  const safeUrl = toSafeUrl(entry.url || "");
  if (!safeUrl || !isImageMedia(entry)) {
    return null;
  }
  const alt = cleanMarkdownToken(entry.alt_text || fallbackTitle || "Dietitian guidance image") || "Dietitian guidance image";
  const caption = cleanMarkdownToken(entry.caption || "");
  const captionToken = caption ? ` "${caption}"` : "";
  return `![${alt}](${safeUrl}${captionToken})`;
}

export function DietitianBlogEditorPage() {
  const { blogId = "" } = useParams();
  const navigate = useNavigate();
  const { session } = useShellSession();
  const isDietitian = Boolean(session.user?.isDietitian);

  const isEdit = useMemo(() => Boolean(blogId), [blogId]);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [isBodyDragActive, setIsBodyDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [postFormat, setPostFormat] = useState<PostFormat>("ARTICLE");
  const [structuredContent, setStructuredContent] = useState<BlogStructuredContent>(createEmptyRecipeStructuredContent());
  const [tagsText, setTagsText] = useState("");
  const [status, setStatus] = useState<PublishStatus>("DRAFT");
  const [media, setMedia] = useState<BlogMedia[]>([]);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [pendingMediaFiles, setPendingMediaFiles] = useState<File[]>([]);
  const [mediaCaption, setMediaCaption] = useState("");
  const [mediaAltText, setMediaAltText] = useState("");

  const bodyInputRef = useRef<HTMLTextAreaElement | null>(null);

  const effectiveCoverImage = coverImageUrl.trim() || firstImageMediaUrl(media);
  const previewMedia = orderMediaByCover(media, effectiveCoverImage);
  const sanitizedStructuredContent = sanitizeStructuredContentForSave(structuredContent);
  const previewHeroMedia = buildHeroMedia(effectiveCoverImage, previewMedia);
  const isRecipeFormat = postFormat === "RECIPE";

  useEffect(() => {
    if (!isEdit || !blogId || !isDietitian) {
      return;
    }
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const blog = await fetchBlogDetail(blogId);
        if (cancelled) {
          return;
        }
        const loadedMedia = Array.isArray(blog.media) ? blog.media : [];
        const nextPostFormat = resolveBlogDisplayStyle(blog);
        setTitle(blog.title || "");
        setSummary(blog.summary || "");
        setBody(blog.body || "");
        setPostFormat(nextPostFormat);
        setStructuredContent(nextPostFormat === "RECIPE" && blog.structuredContent ? blog.structuredContent : createEmptyRecipeStructuredContent());
        setTagsText((blog.tags || []).join(", "));
        setStatus(((blog.status || "DRAFT").toUpperCase() === "PUBLISHED" ? "PUBLISHED" : "DRAFT") as PublishStatus);
        setMedia(loadedMedia);
        setCoverImageUrl((blog.hero_media?.url || firstImageMediaUrl(loadedMedia) || "").trim());
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : "Unable to load blog for editing.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [isEdit, blogId, isDietitian]);

  function resetPendingUploads(): void {
    setPendingMediaFiles([]);
    setMediaCaption("");
    setMediaAltText("");
  }

  function insertAtCursor(snippet: string): void {
    const textarea = bodyInputRef.current;
    if (!textarea) {
      setBody((current) => `${current}${snippet}`);
      return;
    }

    const start = textarea.selectionStart ?? body.length;
    const end = textarea.selectionEnd ?? body.length;
    const nextBody = `${body.slice(0, start)}${snippet}${body.slice(end)}`;
    setBody(nextBody);

    const cursorPosition = start + snippet.length;
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    });
  }

  function insertTemplate(snippet: string): void {
    setSuccess(null);
    insertAtCursor(snippet);
  }

  function handleEmbedMedia(entry: BlogMedia): void {
    const snippet = toImageEmbedMarkdown(entry, title);
    if (!snippet) {
      setError("This media item does not include an embeddable image URL.");
      return;
    }
    insertAtCursor(`\n\n${snippet}\n\n`);
    setError(null);
    setSuccess("Image embed inserted in the post body.");
  }

  function buildHeroMedia(coverUrl: string, sourceMedia: BlogMedia[]): BlogMedia | null {
    const normalizedCoverUrl = coverUrl.trim();
    if (!normalizedCoverUrl) {
      return null;
    }
    const existing = sourceMedia.find((entry) => (entry.url || "").trim() === normalizedCoverUrl && isImageMedia(entry));
    if (existing) {
      return {
        ...existing,
        type: existing.type || "IMAGE",
      };
    }
    return {
      type: "IMAGE",
      url: normalizedCoverUrl,
      alt_text: `${title.trim() || "Dietitian blog"} cover image`,
    };
  }

  async function uploadFiles(files: File[], options?: { caption?: string | null; altText?: string | null }): Promise<BlogMedia[] | null> {
    const cleanFiles = files.filter((file) => isSupportedMediaFile(file));
    if (cleanFiles.length === 0) {
      setError("Only image and video files are supported.");
      return null;
    }

    const formData = new FormData();
    for (const file of cleanFiles) {
      formData.append("files", file);
    }
    if (options?.caption?.trim()) {
      formData.append("caption", options.caption.trim());
    }
    if (options?.altText?.trim()) {
      formData.append("alt_text", options.altText.trim());
    }

    setUploadingMedia(true);
    try {
      const uploaded = await uploadBlogMedia(formData);
      let nextMediaSnapshot: BlogMedia[] = [];
      setMedia((current) => {
        nextMediaSnapshot = [...current, ...uploaded];
        return nextMediaSnapshot;
      });
      if (!coverImageUrl.trim()) {
        const firstUploadedImage = firstImageMediaUrl(nextMediaSnapshot);
        if (firstUploadedImage) {
          setCoverImageUrl(firstUploadedImage);
        }
      }
      return uploaded;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to upload media.");
      return null;
    } finally {
      setUploadingMedia(false);
    }
  }

  async function handleUploadMediaNow(): Promise<void> {
    if (!isDietitian || uploadingMedia || saving || pendingMediaFiles.length === 0) {
      return;
    }
    setError(null);
    setSuccess(null);
    const uploaded = await uploadFiles(pendingMediaFiles, {
      caption: mediaCaption,
      altText: mediaAltText,
    });

    if (uploaded === null) {
      return;
    }

    trackEvent("blog_media_uploaded", { count: uploaded.length });
    resetPendingUploads();
    if (uploaded.length > 0) {
      setSuccess(`${uploaded.length} media item${uploaded.length === 1 ? "" : "s"} uploaded.`);
    }
  }

  async function handleInlineUploadAndEmbed(files: File[], source: "pasted" | "dropped"): Promise<void> {
    if (saving || uploadingMedia) {
      return;
    }
    setError(null);
    setSuccess(null);

    const uploaded = await uploadFiles(files);
    if (uploaded === null) {
      return;
    }

    const snippets = uploaded
      .map((entry) => toImageEmbedMarkdown(entry, title))
      .filter((entry): entry is string => Boolean(entry));

    if (snippets.length > 0) {
      insertAtCursor(`\n\n${snippets.join("\n\n")}\n\n`);
    }

    setSuccess(
      snippets.length > 0
        ? `${source === "pasted" ? "Pasted" : "Dropped"} images uploaded and embedded.`
        : `${source === "pasted" ? "Pasted" : "Dropped"} media uploaded.`,
    );
  }

  function handleBodyPaste(event: ReactClipboardEvent<HTMLTextAreaElement>): void {
    if (!isDietitian) {
      return;
    }

    const files = Array.from(event.clipboardData.files || []).filter((file) => isSupportedMediaFile(file));
    if (files.length > 0) {
      event.preventDefault();
      void handleInlineUploadAndEmbed(files, "pasted");
      return;
    }

    const html = event.clipboardData.getData("text/html");
    if (html && html.trim()) {
      const markdown = htmlToMarkdown(html);
      if (markdown) {
        event.preventDefault();
        insertAtCursor(`\n\n${markdown}\n\n`);
        setError(null);
        setSuccess("Rich content pasted with formatting preserved.");
      }
    }
  }

  function handleBodyDragEnter(event: ReactDragEvent<HTMLTextAreaElement>): void {
    event.preventDefault();
    setIsBodyDragActive(true);
  }

  function handleBodyDragOver(event: ReactDragEvent<HTMLTextAreaElement>): void {
    event.preventDefault();
    setIsBodyDragActive(true);
  }

  function handleBodyDragLeave(event: ReactDragEvent<HTMLTextAreaElement>): void {
    event.preventDefault();
    setIsBodyDragActive(false);
  }

  function handleBodyDrop(event: ReactDragEvent<HTMLTextAreaElement>): void {
    event.preventDefault();
    setIsBodyDragActive(false);

    const droppedFiles = Array.from(event.dataTransfer.files || []).filter((file) => isSupportedMediaFile(file));
    if (droppedFiles.length > 0) {
      void handleInlineUploadAndEmbed(droppedFiles, "dropped");
      return;
    }

    const html = event.dataTransfer.getData("text/html");
    if (html && html.trim()) {
      const markdown = htmlToMarkdown(html);
      if (markdown) {
        insertAtCursor(`\n\n${markdown}\n\n`);
        setError(null);
        setSuccess("Dropped rich content inserted into the post.");
        return;
      }
    }

    const plainText = event.dataTransfer.getData("text/plain");
    if (plainText && plainText.trim()) {
      insertAtCursor(`\n${plainText.trim()}\n`);
      setError(null);
      setSuccess("Dropped text inserted into the post.");
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!isDietitian || saving) {
      return;
    }
    if (!title.trim()) {
      setError("A clear title is required.");
      return;
    }
    if (!isRecipeFormat && !body.trim()) {
      setError("Add post content before saving.");
      return;
    }

    const structuredPayload = sanitizeStructuredContentForSave(structuredContent);
    if (isRecipeFormat) {
      const totalIngredientLines = structuredPayload.ingredients.reduce((count, section) => count + section.items.length, 0);
      if (totalIngredientLines === 0) {
        setError("Add at least one ingredient line for a recipe post.");
        return;
      }
      if (structuredPayload.directions.length === 0) {
        setError("Add at least one direction step for a recipe post.");
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    let mediaPayload: BlogMedia[] = [...media];

    if (pendingMediaFiles.length > 0) {
      const uploaded = await uploadFiles(pendingMediaFiles, {
        caption: mediaCaption,
        altText: mediaAltText,
      });
      if (uploaded === null) {
        setSaving(false);
        return;
      }
      mediaPayload = [...mediaPayload, ...uploaded];
      resetPendingUploads();
    }

    const resolvedCoverUrl = (coverImageUrl.trim() || firstImageMediaUrl(mediaPayload)).trim();
    const orderedMedia = orderMediaByCover(mediaPayload, resolvedCoverUrl);
    const heroMedia = buildHeroMedia(resolvedCoverUrl, orderedMedia);
    const recipeFallbackBody = buildRecipeFallbackBody(structuredPayload);

    const payload = {
      title: title.trim(),
      summary: summary.trim() || null,
      body: isRecipeFormat ? body.trim() || recipeFallbackBody : body.trim(),
      tags: tagsFromText(tagsText),
      status,
      hero_media: heroMedia,
      media: orderedMedia,
      structuredContent: isRecipeFormat ? structuredPayload : null,
    };

    try {
      const blog = isEdit && blogId ? await updateBlog(blogId, payload) : await createBlog(payload);
      trackEvent("blog_post_saved", { is_edit: isEdit, status, format: postFormat, blog_id: blog.id });
      const persistedMedia = Array.isArray(blog.media) ? blog.media : orderedMedia;
      setMedia(persistedMedia);
      const nextPostFormat = resolveBlogDisplayStyle(blog);
      setPostFormat(nextPostFormat);
      setStructuredContent(
        nextPostFormat === "RECIPE"
          ? blog.structuredContent || structuredPayload
          : createEmptyRecipeStructuredContent(),
      );
      setCoverImageUrl((blog.hero_media?.url || resolvedCoverUrl || firstImageMediaUrl(persistedMedia) || "").trim());
      if ((blog.status || "").toUpperCase() === "PUBLISHED") {
        navigate(`/app/dietitian-says/${blog.id}`);
        return;
      }
      setSuccess("Draft saved.");
      if (!isEdit) {
        navigate(`/app/dietitian-says/${blog.id}/edit`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save blog post.");
    } finally {
      setSaving(false);
    }
  }

  if (!isDietitian) {
    return (
      <div data-testid="dietitian-blog-editor-page" className="space-y-4 pb-4">
        <PageHeader title="Dietitian authoring" subtitle="Dietitian access is required to create or edit posts." />
        <ErrorState message="Your account does not have publishing permissions." />
      </div>
    );
  }

  return (
    <div data-testid="dietitian-blog-editor-page" className="space-y-4 pb-4">
      <PageHeader
        title={isEdit ? "Edit dietitian post" : "Create dietitian post"}
        subtitle="Author a standard article or a structured recipe detail for PantryRx members."
        right={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/app/dietitian-says/mine" className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
              Back to drafts
            </Link>
            <Link to="/app/dietitian-says" className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
              Open feed
            </Link>
          </div>
        }
      />

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-40" />
          <Skeleton className="h-72" />
        </div>
      ) : (
        <form className="space-y-3" onSubmit={(event) => void handleSubmit(event)}>
          <Card>
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div className="space-y-3">
                <TextField label="Title" value={title} placeholder="Add a clear headline" onChange={setTitle} />
                <label className="flex flex-col gap-2 text-sm">
                  <span className="font-medium text-[var(--pr-text-muted)]">Summary</span>
                  <textarea
                    value={summary}
                    onChange={(event) => setSummary(event.target.value)}
                    rows={3}
                    placeholder="Short teaser shown in the feed."
                    className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm outline-none ring-[var(--pr-accent)] transition focus:ring-2"
                  />
                </label>

                <div className="space-y-2">
                  <span className="text-sm font-medium text-[var(--pr-text-muted)]">Post format</span>
                  <SegmentedControl
                    value={postFormat}
                    options={[
                      { value: "ARTICLE", label: "Article" },
                      { value: "RECIPE", label: "Recipe" },
                    ]}
                    onChange={(value) => {
                      setPostFormat(value);
                      if (value === "RECIPE") {
                        setStructuredContent((current) => ({ ...current, displayStyle: "RECIPE" }));
                      }
                    }}
                  />
                  <p className="text-xs text-[var(--pr-text-muted)]">
                    {isRecipeFormat
                      ? "Recipe posts render with servings, stats, ingredient sections, directions, and notes."
                      : "Article posts render as rich long-form guidance with Markdown-friendly body content."}
                  </p>
                </div>

                {isRecipeFormat ? (
                  <RecipeStructuredEditor
                    body={body}
                    onBodyChange={setBody}
                    structuredContent={structuredContent}
                    onChange={setStructuredContent}
                  />
                ) : (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-[var(--pr-text-muted)]">Body</span>
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          onClick={() => insertTemplate("## Section heading\n\n")}
                          className="rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-2 py-1 text-xs font-semibold"
                        >
                          H2
                        </button>
                        <button
                          type="button"
                          onClick={() => insertTemplate("### Subheading\n\n")}
                          className="rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-2 py-1 text-xs font-semibold"
                        >
                          H3
                        </button>
                        <button
                          type="button"
                          onClick={() => insertTemplate("\n- Key point\n- Supporting point\n")}
                          className="rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-2 py-1 text-xs font-semibold"
                        >
                          Bullet list
                        </button>
                        <button
                          type="button"
                          onClick={() => insertTemplate("\n1. First step\n2. Next step\n")}
                          className="rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-2 py-1 text-xs font-semibold"
                        >
                          Numbered
                        </button>
                        <button
                          type="button"
                          onClick={() => insertTemplate("\n> Practical tip: make one pantry swap this week.\n")}
                          className="rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-2 py-1 text-xs font-semibold"
                        >
                          Quote
                        </button>
                      </div>
                    </div>

                    <div
                      className={`rounded-xl border bg-[var(--pr-surface)] transition focus-within:ring-2 focus-within:ring-[var(--pr-accent)] ${
                        isBodyDragActive
                          ? "border-[color-mix(in_srgb,var(--pr-accent)_70%,transparent)] shadow-[0_0_0_2px_color-mix(in_srgb,var(--pr-accent)_20%,transparent)]"
                          : "border-[var(--pr-border)]"
                      }`}
                    >
                      <textarea
                        ref={bodyInputRef}
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                        onPaste={handleBodyPaste}
                        onDragEnter={handleBodyDragEnter}
                        onDragOver={handleBodyDragOver}
                        onDragLeave={handleBodyDragLeave}
                        onDrop={handleBodyDrop}
                        rows={18}
                        placeholder={'Paste from docs/web, or drag images directly here. Markdown works too: ![Oat pantry jars](https://image-url "Caption")'}
                        className="w-full resize-y rounded-xl border-0 bg-transparent px-3 py-2 text-sm outline-none"
                      />
                    </div>

                    <p className="text-xs text-[var(--pr-text-muted)]">
                      Copy/paste rich text keeps structure, paste screenshots uploads instantly, and drag/drop images auto-embed in the body.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Card flat>
                  <p className="text-sm font-semibold">Publishing</p>
                  <label className="mt-2 flex flex-col gap-2 text-sm">
                    <span className="font-medium text-[var(--pr-text-muted)]">Status</span>
                    <select
                      value={status}
                      onChange={(event) => setStatus((event.target.value as PublishStatus) || "DRAFT")}
                      className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="PUBLISHED">Published</option>
                    </select>
                  </label>
                  <TextField label="Tags" value={tagsText} placeholder="Nutrition, pantry, habits" onChange={setTagsText} />
                </Card>

                <Card flat>
                  <p className="text-sm font-semibold">Cover image</p>
                  <TextField
                    label="Cover image URL"
                    value={coverImageUrl}
                    placeholder="https://... or /static/..."
                    onChange={setCoverImageUrl}
                  />
                  {effectiveCoverImage ? (
                    <div className="mt-2 overflow-hidden rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)]">
                      <img
                        src={effectiveCoverImage}
                        alt={resolveMediaAltText(previewHeroMedia, title || "Selected cover")}
                        className="h-36 w-full object-cover"
                      />
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-[var(--pr-text-muted)]">Upload or embed an image to set a cover.</p>
                  )}
                </Card>

                <Card flat>
                  <p className="text-sm font-semibold">Media library</p>
                  <p className="mt-1 text-xs text-[var(--pr-text-muted)]">
                    {isRecipeFormat
                      ? "Upload media, choose the hero first, and keep the published media order exactly how you want it."
                      : "Upload media, then set cover or insert images into the body with one click."}
                  </p>
                  <label className="mt-2 flex flex-col gap-2 text-sm">
                    <span className="font-medium text-[var(--pr-text-muted)]">Files</span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={(event) => setPendingMediaFiles(Array.from(event.target.files ?? []))}
                      className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                    />
                  </label>
                  <TextField
                    label="Caption for next upload"
                    value={mediaCaption}
                    placeholder="Optional caption for uploaded media"
                    onChange={setMediaCaption}
                  />
                  <TextField
                    label="Alt text for next image upload"
                    value={mediaAltText}
                    placeholder="Image accessibility text"
                    onChange={setMediaAltText}
                  />

                  {pendingMediaFiles.length > 0 ? (
                    <>
                      <div className="mt-2 space-y-1 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-xs text-[var(--pr-text-muted)]">
                        {pendingMediaFiles.map((file) => (
                          <p key={`${file.name}-${file.size}`}>{file.name}</p>
                        ))}
                      </div>
                      <Button
                        type="button"
                        className="mt-2 w-full"
                        variant="secondary"
                        disabled={uploadingMedia || saving}
                        onClick={() => void handleUploadMediaNow()}
                      >
                        {uploadingMedia ? "Uploading..." : "Upload selected media"}
                      </Button>
                    </>
                  ) : null}

                  {media.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {media.map((entry, index) => {
                        const mediaUrl = (entry.url || "").trim();
                        const canEmbed = Boolean(mediaUrl) && isImageMedia(entry);
                        const isCover = Boolean(mediaUrl) && mediaUrl === coverImageUrl.trim();
                        return (
                          <div
                            key={mediaIdentifier(entry, index)}
                            className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] p-2"
                          >
                            <div className="flex gap-2">
                              {canEmbed ? (
                                <img src={mediaUrl} alt={entry.alt_text || "Uploaded media"} className="h-14 w-14 rounded-lg object-cover" />
                              ) : (
                                <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-[var(--pr-border)] text-[10px] font-semibold text-[var(--pr-text-muted)]">
                                  {(entry.type || "MEDIA").toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-xs font-semibold">
                                  {(entry.type || "MEDIA").toUpperCase()} · {mediaUrl || entry.key || "uploaded"}
                                </p>
                                <p className="mt-1 text-xs text-[var(--pr-text-muted)]">{mediaCaptionLabel(entry)}</p>
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {canEmbed ? (
                                <button
                                  type="button"
                                  onClick={() => setCoverImageUrl(mediaUrl)}
                                  className={`rounded-lg px-2 py-1 text-xs font-semibold ${
                                    isCover
                                      ? "bg-[color-mix(in_srgb,var(--pr-accent)_20%,transparent)] text-[var(--pr-accent-deep)]"
                                      : "border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]"
                                  }`}
                                >
                                  {isCover ? "Cover selected" : "Set cover"}
                                </button>
                              ) : null}
                              {canEmbed && !isRecipeFormat ? (
                                <button
                                  type="button"
                                  onClick={() => handleEmbedMedia(entry)}
                                  className="rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-2 py-1 text-xs font-semibold"
                                >
                                  Embed in body
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() =>
                                  setMedia((current) => {
                                    const removed = current[index];
                                    const next = current.filter((_, mediaIndex) => mediaIndex !== index);
                                    if (removed && (removed.url || "").trim() === coverImageUrl.trim()) {
                                      setCoverImageUrl(firstImageMediaUrl(next));
                                    }
                                    return next;
                                  })
                                }
                                className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </Card>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold">Live preview</h3>
              <p className="text-xs text-[var(--pr-text-muted)]">Rendered like published post detail on web and phone.</p>
            </div>
            <div className="mt-3 space-y-4">
              {previewHeroMedia?.url ? (
                <div className="overflow-hidden rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]">
                  <img
                    src={previewHeroMedia.url}
                    alt={resolveMediaAltText(previewHeroMedia, title || "Post cover")}
                    className="h-44 w-full object-cover sm:h-52"
                  />
                </div>
              ) : null}
              <div>
                {isRecipeFormat ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--pr-text-muted)]">
                    {sanitizedStructuredContent.servingText || "Structured recipe"}
                  </p>
                ) : null}
                <h2 className="pr-display text-2xl font-semibold sm:text-3xl">{title || "Untitled post"}</h2>
                <p className="mt-2 text-sm text-[var(--pr-text-muted)]">
                  {summary || "Add a summary to show a concise teaser in the feed."}
                </p>
              </div>
              {isRecipeFormat ? (
                <BlogRecipeSections
                  structuredContent={sanitizedStructuredContent}
                  emptyMessage="Add servings, ingredients, directions, and notes to see the recipe detail layout."
                />
              ) : (
                <BlogRichContent body={body} emptyMessage="Start writing to see a live rich preview." />
              )}
            </div>
          </Card>

          {error ? <ErrorState message={error} /> : null}
          {success ? (
            <Card flat>
              <p className="text-sm font-semibold text-[var(--pr-accent-deep)]">{success}</p>
            </Card>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="submit" disabled={saving || uploadingMedia}>
              {saving ? "Saving..." : "Save post"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
